import { useState } from "react";
import { Loader2, ArrowRight, Check } from "lucide-react";
import { upsertProfile } from "../services/profile";
import { setSetting } from "../services/settings";
import { useT } from "../services/i18n";

const UI_LANGS = [
  { code: "fr", label: "FR" },
  { code: "en", label: "EN" },
  { code: "ru", label: "RU" },
];

export default function OnboardingScreen({ onDone }) {
  const { t, lang } = useT();
  const [firstName, setFirstName] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [consent, setConsent] = useState(false);
  const [contactOk, setContactOk] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!firstName.trim() || !consent) {
      setError(t("onb_err"));
      return;
    }
    setSaving(true);
    setError("");
    try {
      const year = parseInt(birthYear, 10);
      await upsertProfile({
        first_name: firstName.trim(),
        birth_year: Number.isFinite(year) ? year : null,
        consent_accepted: true,
        consent_at: new Date().toISOString(),
        consent_version: "v1",
        contact_ok: contactOk,
      });
      onDone();
    } catch (err) {
      setError(t("onb_save_err"));
    } finally {
      setSaving(false);
    }
  };

  const privacyBlocks = [
    { title: t("priv_1_title"), body: t("priv_1_body") },
    { title: t("priv_2_title"), body: t("priv_2_body") },
    { title: t("priv_3_title"), body: t("priv_3_body") },
  ];

  return (
    <div
      className="mx-auto flex min-h-[100dvh] w-full max-w-md flex-col px-7 pb-[max(28px,env(safe-area-inset-bottom))] pt-[max(40px,env(safe-area-inset-top))]"
      data-testid="onboarding-screen"
    >
      <div className="mb-6 flex justify-end" data-testid="onb-lang-switch">
        <div className="inline-flex gap-1 rounded-full bg-[var(--rmr-chip)] p-1">
          {UI_LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              data-testid={`onb-lang-${l.code}`}
              onClick={() => {
                setSetting("uiLang", l.code);
                window.dispatchEvent(new Event("lang-change"));
              }}
              className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${
                lang === l.code ? "bg-[var(--rmr-surface)] text-[var(--rmr-accent)] shadow-sm" : "text-[var(--rmr-text-2)]"
              }`}
            >
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <img src="/butterfly-nobg.png" alt="" className="mb-4 h-16 w-16 object-contain" />
      <h1 className="brand text-3xl font-bold text-[var(--rmr-text)]">{t("onb_title")}</h1>
      <p className="mt-2 text-[15px] leading-relaxed text-[var(--rmr-text-2)]">{t("onb_sub")}</p>

      <form onSubmit={handleSubmit} className="mt-7 flex flex-1 flex-col">
        <label className="text-sm font-semibold text-[var(--rmr-text)]">{t("onb_firstname")}</label>
        <input
          data-testid="onb-firstname-input"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder={t("onb_firstname_ph")}
          autoComplete="given-name"
          className="mt-2 w-full rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] px-4 py-3.5 text-[15px] text-[var(--rmr-text)] outline-none transition-colors focus:border-[var(--rmr-accent)]"
        />

        {/* Consentement requis */}
        <button
          type="button"
          data-testid="onb-consent-toggle"
          onClick={() => setConsent((v) => !v)}
          className="mt-5 flex items-start gap-3 text-left"
        >
          <span
            className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border transition-colors ${
              consent ? "border-[var(--rmr-accent)] bg-[var(--rmr-accent)] text-white" : "border-[var(--rmr-border)] bg-[var(--rmr-surface)]"
            }`}
          >
            {consent && <Check size={15} />}
          </span>
          <span className="text-sm leading-relaxed text-[var(--rmr-text)]">
            {t("onb_consent")}
            <span
              role="link"
              data-testid="onb-privacy-link"
              onClick={(e) => {
                e.stopPropagation();
                setShowPrivacy((v) => !v);
              }}
              className="font-semibold text-[var(--rmr-accent)] underline"
            >
              {t("onb_consent_link")}
            </span>
            .
          </span>
        </button>

        {showPrivacy && (
          <div className="mt-3 space-y-3 rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] p-4" data-testid="onb-privacy-panel">
            {privacyBlocks.map((b, i) => (
              <div key={i}>
                <p className="text-sm font-semibold text-[var(--rmr-text)]">{b.title}</p>
                <p className="mt-1 text-xs leading-relaxed text-[var(--rmr-text-2)]">{b.body}</p>
              </div>
            ))}
          </div>
        )}

        {/* Optionnel */}
        <p className="mt-7 text-xs font-semibold uppercase tracking-wide text-[var(--rmr-text-3)]">{t("onb_optional")}</p>

        <label className="mt-3 text-sm font-semibold text-[var(--rmr-text)]">{t("onb_birthyear")}</label>
        <input
          data-testid="onb-birthyear-input"
          value={birthYear}
          onChange={(e) => setBirthYear(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
          placeholder={t("onb_birthyear_ph")}
          inputMode="numeric"
          className="mt-2 w-full rounded-2xl border border-[var(--rmr-border)] bg-[var(--rmr-surface)] px-4 py-3.5 text-[15px] text-[var(--rmr-text)] outline-none transition-colors focus:border-[var(--rmr-accent)]"
        />

        <button
          type="button"
          data-testid="onb-contact-toggle"
          onClick={() => setContactOk((v) => !v)}
          className="mt-4 flex items-start gap-3 text-left"
        >
          <span
            className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-md border transition-colors ${
              contactOk ? "border-[var(--rmr-accent)] bg-[var(--rmr-accent)] text-white" : "border-[var(--rmr-border)] bg-[var(--rmr-surface)]"
            }`}
          >
            {contactOk && <Check size={15} />}
          </span>
          <span className="text-sm leading-relaxed text-[var(--rmr-text-2)]">{t("onb_contact")}</span>
        </button>

        {error && (
          <p className="mt-4 text-sm text-[var(--rmr-danger)]" data-testid="onb-error">
            {error}
          </p>
        )}

        <div className="flex-1" />
        <button
          type="submit"
          disabled={saving}
          data-testid="onb-submit-btn"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-[var(--rmr-accent)] py-3.5 text-[15px] font-semibold text-white transition-transform active:scale-[0.98] disabled:opacity-50"
        >
          {saving ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
          {t("onb_cta")}
        </button>
      </form>
    </div>
  );
}
